import { useMemo, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useTimeZones, type TimeZoneRow } from './systemMasters';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ System Management ▸ Time Zones — read-only reference list
 * (legacy TimeZones_ListView is New/Delete-disabled; it's a fixed lookup).
 */
export function TimeZonesPage({ onHome }: Props) {
  const { data: rows = [], isFetching, refetch } = useTimeZones();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.timeZone, r.utc].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const columns: AppColumn<TimeZoneRow>[] = [
    { field: 'timeZone', header: 'Time Zone', minWidth: 320, text: (r) => r.timeZone ?? '',
      compare: (a, b) => (a.timeZone ?? '').localeCompare(b.timeZone ?? '', undefined, { sensitivity: 'base' }) },
    { field: 'utc', header: 'UTC', width: 140, sortable: false, text: (r) => r.utc ?? '' },
    { field: 'active', header: 'Active', width: 110, align: 'center', sortable: false, text: (r) => (r.active ? 'Yes' : 'No'),
      render: (r) => <span className={`cellcheck${r.active ? ' on' : ''}`} role="img" aria-label={r.active ? 'Enabled' : 'Disabled'} /> },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Time Zones</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-timezones" auditFormName="Time Zones" loadMs={loadMs ?? null} loading={isFetching} />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="TimeZones" headers={['Time Zone', 'UTC', 'Active']} rows={visible.map((r) => [r.timeZone ?? '', r.utc ?? '', r.active ? 'Yes' : 'No'])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card">
          <AppDataGrid<TimeZoneRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            initialSort={{ key: 'timeZone', dir: 'asc' }} loading={isFetching} emptyText="No time zones found." />
        </div>
      </div>
    </div>
  );
}
